import React from "react";
import {
  Badge,
  Button,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";

type Props = {
  isOpen: boolean;
  busy: boolean;
  reason: string;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

const PRESET_KEYS = ["audience", "subject", "schedule", "setup", "other"] as const;

/**
 * Modal reaproveitável: rejeitar com motivo rápido evita 403 “mudo” entre editor e aprovador.
 */
const SendApprovalRejectModal = ({
  isOpen,
  busy,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={isOpen} toggle={() => !busy && onClose()} centered>
      <ModalHeader toggle={() => !busy && onClose()}>
        {t("sending.approval.rejectTitle")}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <p className="text-muted small mb-2">
            {t("sending.approval.presets.title")}
          </p>
          <div className="d-flex flex-wrap gap-2">
            {PRESET_KEYS.map((preset) => {
              const value = t(`sending.approval.presets.${preset}`);
              const active = reason.trim() === value.trim();

              return (
                <Button
                  key={preset}
                  color={active ? "primary" : "light"}
                  size="sm"
                  type="button"
                  disabled={busy}
                  onClick={() => onReasonChange(value)}
                >
                  {active ? (
                    <Badge color="dark" pill className="me-1">
                      {t("sending.approval.presets.selected")}
                    </Badge>
                  ) : null}
                  {value}
                </Button>
              );
            })}
          </div>
        </div>

        <Label htmlFor="rejection-reason">{t("sending.approval.reason")}</Label>
        <Input
          id="rejection-reason"
          type="textarea"
          rows={3}
          value={reason}
          disabled={busy}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder={t("sending.approval.reasonPlaceholder")}
        />
        <p className="text-muted small mb-0 mt-1">
          {t("sending.approval.reasonHelp")}
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={busy} onClick={onClose}>
          {t("sending.modals.cancel")}
        </Button>
        <Button color="danger" disabled={busy} onClick={onConfirm}>
          {busy ? <Spinner size="sm" /> : t("sending.approval.confirmReject")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SendApprovalRejectModal;
